/**
 * CoreTransactionTable — shared transaction table used across CreditCardDetail,
 * CashAccountDetail, ResiPropertyDetail, VehicleDetail, and ExpensesPage.
 *
 * Props:
 *   transactions        {Array}   Pre-processed flat list (parent/child rows already flattened with _isChild flags)
 *   sourceType          {string}  'revolver' | 'cash' | 'property' | 'vehicle' | 'expenses'
 *   apiBase             {string}  Base API path for PATCH/DELETE, e.g. '/balance_sheet/financing/revolvers'
 *   columns             {Array}   Ordered list of column keys to render (see COLUMN_KEYS below)
 *   capabilities        {Object}  Feature flags: { inlineEdit, receipts, reimbursement, incomeSplit, incomeLink, debtDisbursement, split, standaloneSeriesModal, delete, export }
 *   expenseCategories   {Object}  { items: [...], noneItem: {...} } — category tree from parent
 *   categoryLabelMap    {Map}     Map<string, string> of value→label
 *   onUpdateCategory    {fn}      (transactionId, category) => void — parent handles side effects
 *   onDelete            {fn}      (transactionId) => void
 *   onRefresh           {fn}      () => void — called after any mutation that needs a data refresh
 *   exportFilename      {string}  Base filename for Excel export
 *   allAssets           {Array}   For asset label resolution
 *   assetMaps           {Object}  { vehicleMap, propertyMap, ... }
 *   showCardColumn      {bool}    Show card_display column (CC "All Cards" view)
 *   deleting            {Set}     Set of transaction IDs currently being deleted
 *   receiptBaseUrl      {string}  Base URL for receipt upload/view, e.g. '/balance_sheet/financing/revolvers/transactions'
 */

import React, { useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { exportToExcel } from '../utils/exportUtils';
import { buildAssetLabel, getDomainForCategory } from '../utils/assetLabelHelper';
import { useReceipts } from '../hooks/useReceipts';
import AssetExpenseLinkModal from './AssetExpenseLinkModal.jsx';
import DebtDisbursementLinkModal from './DebtDisbursementLinkModal.jsx';
import IncomeSplitModal from './IncomeSplitModal';
import IncomeLinkModal from './IncomeLinkModal.jsx';
import ReimbursementLinkModal from './ReimbursementLinkModal';
import ReceiptModal from './ReceiptModal';
import SplitTransactionModal from './SplitTransactionModal';
import StandaloneSeriesModal from './StandaloneSeriesModal';

// ─── Editable Cell ────────────────────────────────────────────────────────────
const MemoizedEditableCell = React.memo(({ transaction, field, displayValue, isEditing, onStartEdit, onCancel, onSave, saving }) => {
  const [tempValue, setTempValue] = useState(displayValue);

  React.useEffect(() => {
    if (isEditing) setTempValue(displayValue);
  }, [isEditing, displayValue]);

  if (isEditing) {
    return (
      <div className="d-flex align-items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <input
          type={field === 'transaction_date' || field === 'post_date' ? 'date' : field === 'amount' ? 'number' : 'text'}
          className="form-control form-control-sm"
          value={tempValue}
          onChange={(e) => setTempValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') onSave(tempValue); else if (e.key === 'Escape') onCancel(); }}
          autoFocus
          step={field === 'amount' ? '0.01' : undefined}
        />
        <button className="btn btn-sm btn-success" onClick={() => onSave(tempValue)} disabled={saving}>{saving ? '...' : '✓'}</button>
        <button className="btn btn-sm btn-secondary" onClick={onCancel} disabled={saving}>✗</button>
      </div>
    );
  }

  return (
    <div
      className="editable-cell"
      style={{ position: 'relative', cursor: 'pointer', padding: '4px', transition: 'background-color 0.2s' }}
      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#1f2937'; e.currentTarget.style.borderRadius = '4px'; }}
      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); onStartEdit(transaction.id, field, transaction[field]); }}
    >
      <span>{displayValue}</span>
      <span style={{ opacity: 0, marginLeft: '8px', fontSize: '12px', color: '#6c757d' }}>✏️</span>
    </div>
  );
});

// ─── Helpers ──────────────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const SortIcon = ({ field, sortField, sortDirection }) => {
  if (sortField !== field) return <span className="text-muted ms-1" style={{ cursor: 'pointer' }}>⇅</span>;
  return <span className="text-primary ms-1" style={{ cursor: 'pointer' }}>{sortDirection === 'asc' ? '↑' : '↓'}</span>;
};

const getCategoryStyle = (category) => {
  const styles = {
    income: { backgroundColor: '#E8F5E9', color: '#2E7D32', borderColor: '#2E7D32' },
    transfer: { backgroundColor: '#E3F2FD', color: '#1976D2', borderColor: '#1976D2' },
    payments: { backgroundColor: '#E8F5E8', color: '#2E7D32', borderColor: '#2E7D32' },
    rewards_cashback: { backgroundColor: '#FFF8E1', color: '#F57C00', borderColor: '#F57C00' },
    other: { backgroundColor: '#F5F5F5', color: '#616161', borderColor: '#616161' },
    none: { backgroundColor: '#FFFFFF', color: '#000000', borderColor: '#000000' },
  };
  return styles[category] || styles['none'];
};

// ─── Main Component ───────────────────────────────────────────────────────────
const CoreTransactionTable = ({
  transactions = [],
  sourceType = 'cash',
  apiBase = '',
  columns = [],
  capabilities = {},
  expenseCategories = { items: [], noneItem: { value: 'none', label: 'None' } },
  categoryLabelMap = new Map(),
  onUpdateCategory,
  onDelete,
  onRefresh,
  exportFilename = 'Transactions',
  allAssets = [],
  assetMaps = {},
  showCardColumn = false,
  deleting = new Set(),
  receiptBaseUrl = '',
  expandedTxns: expandedTxnsProp = null,
  onToggleExpand = null,
}) => {
  // ── Sort state ──
  const [sortField, setSortField] = useState('transaction_date');
  const [sortDirection, setSortDirection] = useState('desc');

  // ── Inline edit state ──
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // ── Expand/collapse (internal state used only when parent doesn't control it) ──
  const [expandedTxnsInternal, setExpandedTxnsInternal] = useState(new Set());
  const expandedTxns = expandedTxnsProp !== null ? expandedTxnsProp : expandedTxnsInternal;
  const toggleExpand = useCallback((id) => {
    if (onToggleExpand) {
      onToggleExpand(id);
    } else {
      setExpandedTxnsInternal(prev => { const n = new Set([...prev]); n.has(id) ? n.delete(id) : n.add(id); return n; });
    }
  }, [onToggleExpand]);

  // ── Category dropdown state ──
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [expandedParents, setExpandedParents] = useState({});

  // ── Modal state ──
  const [utilityRow, setUtilityRow] = useState(null);
  const [showUtilityModal, setShowUtilityModal] = useState(false);

  const [showSplitModal, setShowSplitModal] = useState(false);
  const [splitRow, setSplitRow] = useState(null);

  const [showIncomeSplitModal, setShowIncomeSplitModal] = useState(false);
  const [incomeSplitRow, setIncomeSplitRow] = useState(null);
  const [incomeSplitDefaultKind, setIncomeSplitDefaultKind] = useState('recurring');

  const [showIncomeLinkModal, setShowIncomeLinkModal] = useState(false);
  const [incomeLinkRow, setIncomeLinkRow] = useState(null);

  const [showDebtDisbursementModal, setShowDebtDisbursementModal] = useState(false);
  const [debtDisbursementRow, setDebtDisbursementRow] = useState(null);

  const [showReimbursementModal, setShowReimbursementModal] = useState(false);
  const [reimbursementRow, setReimbursementRow] = useState(null);

  const [showStandaloneSeriesModal, setShowStandaloneSeriesModal] = useState(false);
  const [standaloneSeriesRow, setStandaloneSeriesRow] = useState(null);

  // ── Receipts ──
  const {
    showReceiptModal,
    receiptPreviewUrl,
    receiptModalTitle,
    openReceipt,
    closeReceipt,
    uploadReceipt,
  } = useReceipts(receiptBaseUrl, onRefresh);

  // ── Close dropdown on outside click ──
  React.useEffect(() => {
    if (!openDropdownId) return;
    const handler = () => setOpenDropdownId(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [openDropdownId]);

  // ── Sort ──
  const handleSort = useCallback((field) => {
    setSortField(prev => {
      if (prev === field) {
        setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
        return prev;
      }
      setSortDirection('asc');
      return field;
    });
  }, []);

  // ── Sorted transactions (parents sorted; children always follow their parent) ──
  const sortedTransactions = useMemo(() => {
    // Separate parents from children
    // Child rows are marked with _isChild:true and carry their parent id in .parent or .parent_id
    const parents = transactions.filter(t => !t._isChild);
    const childrenByParent = {};
    transactions.forEach(t => {
      if (!t._isChild) return;
      const parentId = t.parent ?? t.parent_id ?? null;
      if (parentId != null) {
        if (!childrenByParent[parentId]) childrenByParent[parentId] = [];
        childrenByParent[parentId].push(t);
      }
    });

    // Sort only parent rows
    const compareFn = (a, b) => {
      let av = a[sortField];
      let bv = b[sortField];
      if (sortField === 'amount') { av = parseFloat(av) || 0; bv = parseFloat(bv) || 0; }
      else if (sortField === 'transaction_date' || sortField === 'post_date') { av = new Date(av || 0); bv = new Date(bv || 0); }
      else if (typeof av === 'string') { av = av.toLowerCase(); bv = (bv || '').toLowerCase(); }
      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    };
    parents.sort(compareFn);

    // Re-insert children immediately after their parent
    const result = [];
    parents.forEach(parent => {
      result.push(parent);
      const kids = childrenByParent[parent.id];
      if (kids && kids.length > 0) {
        kids.forEach(child => result.push(child));
      }
    });

    // Any orphaned children (parent not in list) appended at end
    transactions.forEach(t => {
      if ((t._isChild || t.parent_transaction) && !result.includes(t)) {
        result.push(t);
      }
    });

    return result;
  }, [transactions, sortField, sortDirection]);

  // ── Inline edit handlers ──
  const startEditing = useCallback((transactionId, field, currentValue) => {
    setEditingCell({ transactionId, field });
    if (field === 'transaction_date' || field === 'post_date') {
      if (typeof currentValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(currentValue)) {
        setEditValue(currentValue);
      } else {
        const d = new Date(currentValue);
        setEditValue(isNaN(d.getTime()) ? '' : d.toISOString().split('T')[0]);
      }
    } else if (field === 'amount') {
      setEditValue(String(currentValue || ''));
    } else {
      setEditValue(currentValue || '');
    }
  }, []);

  const cancelEditing = useCallback(() => { setEditingCell(null); setEditValue(''); }, []);

  const saveEdit = useCallback(async (overridingValue = null) => {
    if (!editingCell || saving) return;
    setSaving(true);
    try {
      let valueToSave = overridingValue !== null ? overridingValue : editValue;
      if (editingCell.field === 'amount') {
        const n = parseFloat(valueToSave);
        if (isNaN(n)) { alert('Please enter a valid number for amount'); setSaving(false); return; }
        valueToSave = n;
      } else if (editingCell.field === 'description') {
        if (!String(valueToSave).trim()) { alert('Description cannot be empty'); setSaving(false); return; }
        valueToSave = String(valueToSave).trim();
      }
      const endpoint = sourceType === 'revolver'
        ? `/balance_sheet/financing/revolvers/transactions/${editingCell.transactionId}/`
        : `/balance_sheet/assets/cash_accounts/transactions/${editingCell.transactionId}/`;
      await api.patch(endpoint, { [editingCell.field]: valueToSave });
      setEditingCell(null);
      setEditValue('');
      if (onRefresh) await onRefresh();
    } catch (e) {
      console.error('Error saving edit:', e);
      alert('Failed to save changes. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [editingCell, saving, editValue, sourceType, onRefresh]);

  // ── Delete ──
  const handleDelete = useCallback(async (transactionId) => {
    if (!window.confirm('Are you sure you want to delete this transaction? This action cannot be undone.')) return;
    if (onDelete) onDelete(transactionId);
  }, [onDelete]);

  // ── Export ──
  const handleExport = useCallback(() => {
    const cols = [
      { header: 'Date', key: t => t.transaction_date ? new Date(t.transaction_date).toLocaleDateString() : '' },
      { header: 'Description', key: 'description' },
      { header: 'User Description', key: 'user_description' },
      { header: 'Amount', key: 'amount' },
      { header: 'Category', key: 'expense_category' },
      { header: 'Asset Link', key: t => t.linked_asset_label || '' },
      { header: 'Debt Link', key: t => t.linked_debt_name || '' },
    ];
    const dateStr = new Date().toISOString().split('T')[0].replace(/-/g, '_');
    exportToExcel(sortedTransactions, `${exportFilename}_${dateStr}`, cols);
  }, [sortedTransactions, exportFilename]);

  // ── Category dropdown toggle ──
  const toggleDropdown = useCallback((e, txnId) => {
    e.stopPropagation();
    setOpenDropdownId(prev => prev === txnId ? null : txnId);
  }, []);

  // ── Render category dropdown ──
  const renderExpenseCategory = useCallback((transaction) => {
    const getLabelForValue = (val) => categoryLabelMap.get(String(val)) || 'None';
    const isParentExpanded = (parentVal) => !!(expandedParents[transaction.id]?.has(parentVal));
    const toggleParent = (parentVal) => {
      setExpandedParents(prev => {
        const cur = new Set(prev[transaction.id] || []);
        if (cur.has(parentVal)) cur.delete(parentVal); else cur.add(parentVal);
        return { ...prev, [transaction.id]: cur };
      });
    };
    const category = transaction.expense_category || 'none';
    const label = getLabelForValue(category);
    const style = getCategoryStyle(category);
    const isOpen = openDropdownId === transaction.id;

    return (
      <div className="position-relative" style={{ display: 'inline-block' }}>
        <button
          className="btn btn-sm"
          type="button"
          onClick={(e) => toggleDropdown(e, transaction.id)}
          style={{ backgroundColor: style.backgroundColor, color: style.color, borderColor: style.borderColor, borderRadius: '12px', fontSize: '0.75rem', padding: '4px 8px', border: `1px solid ${style.borderColor}`, cursor: 'pointer', whiteSpace: 'nowrap' }}
        >
          {label} ▼
        </button>
        {isOpen && (
          <div
            className="position-absolute"
            style={{ top: '100%', left: 0, zIndex: 1000, minWidth: '320px', backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '0.375rem', boxShadow: '0 0.5rem 1rem rgba(0,0,0,0.3)', marginTop: '2px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ maxHeight: '480px', overflowY: 'auto', padding: '4px 0' }}>
              {expenseCategories.items.map((it) => (
                <div key={it.value}>
                  <button
                    className="w-100 text-start"
                    style={{ display: 'block', padding: '8px 16px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                    onClick={() => {
                      if (Array.isArray(it.children) && it.children.length > 0) {
                        toggleParent(it.value);
                      } else {
                        if (onUpdateCategory) onUpdateCategory(transaction.id, String(it.value));
                        setOpenDropdownId(null);
                      }
                    }}
                  >
                    {(Array.isArray(it.children) && it.children.length > 0) ? (isParentExpanded(it.value) ? '▼ ' : '► ') : ''}
                    {it.label}
                  </button>
                  {Array.isArray(it.children) && it.children.length > 0 && isParentExpanded(it.value) && (
                    <div>
                      {it.children.map(ch => (
                        <button
                          key={ch.value}
                          className="w-100 text-start"
                          style={{ display: 'block', padding: '6px 16px 6px 28px', border: 'none', backgroundColor: '#111827', cursor: 'pointer', fontSize: '0.875rem', color: '#e5e7eb' }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#1f2937'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#111827'}
                          onClick={() => { if (onUpdateCategory) onUpdateCategory(transaction.id, String(ch.value)); setOpenDropdownId(null); }}
                        >
                          {ch.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }, [categoryLabelMap, expenseCategories, openDropdownId, expandedParents, toggleDropdown, onUpdateCategory]);

  // ── Build asset label for a transaction ──
  const buildTxnAssetLabel = useCallback((transaction) => {
    if (!transaction.linked_asset_id) return '';
    const asset = allAssets.find(a => String(a.id) === String(transaction.linked_asset_id));
    if (!asset) return transaction.linked_asset_label || `Asset ${transaction.linked_asset_id}`;
    return buildAssetLabel(asset, { ...assetMaps, transactionDomain: getDomainForCategory(transaction.expense_category) });
  }, [allAssets, assetMaps]);

  // ── Column header definitions ──
  const colHasSort = (key) => ['transaction_date', 'post_date', 'description', 'user_description', 'amount', 'expense_category', 'card_display'].includes(key);

  const colHeader = (key) => {
    const labels = {
      transaction_date: 'Date', post_date: 'Post Date', card_display: 'Card',
      description: 'Description', user_description: 'User Description',
      amount: 'Amount', expense_category: 'Category', source: 'Source',
      expense_group: 'Group', asset_link: 'Asset Link', linked_todo: 'Linked To-Do',
      recurring_series: 'Recurring Series', debt_link: 'Debt Link',
      receipt: 'Receipt', actions: 'Actions',
    };
    return labels[key] || key;
  };

  // ── Render a single cell ──
  const renderCell = (transaction, colKey) => {
    const isEditable = capabilities.inlineEdit;
    const isEditingThis = (field) => editingCell?.transactionId === transaction.id && editingCell?.field === field;

    switch (colKey) {
      case 'transaction_date':
        return isEditable ? (
          <MemoizedEditableCell transaction={transaction} field="transaction_date"
            displayValue={formatDate(transaction.transaction_date)}
            isEditing={isEditingThis('transaction_date')}
            onStartEdit={startEditing} onCancel={cancelEditing} onSave={saveEdit} saving={saving} />
        ) : <span>{formatDate(transaction.transaction_date)}</span>;

      case 'post_date':
        return isEditable ? (
          <MemoizedEditableCell transaction={transaction} field="post_date"
            displayValue={formatDate(transaction.post_date)}
            isEditing={isEditingThis('post_date')}
            onStartEdit={startEditing} onCancel={cancelEditing} onSave={saveEdit} saving={saving} />
        ) : <span>{formatDate(transaction.post_date)}</span>;

      case 'card_display':
        return <span>{transaction.card_display || 'N/A'}</span>;

      case 'description':
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            {!transaction._isChild && transaction.has_children ? (
              <button type="button" className="btn btn-sm btn-outline-secondary"
                onClick={(e) => { e.stopPropagation(); toggleExpand(transaction.id); }}
                style={{ padding: '2px 6px', fontSize: '10px', minWidth: '20px' }}>
                {expandedTxns.has(transaction.id) ? '−' : '+'}
              </button>
            ) : (!transaction._isChild && <span style={{ width: 20 }} />)}
            <div style={transaction._isChild ? { paddingLeft: 24 } : {}}>
              {transaction.is_pending && (
                <span style={{ fontSize: '10px', fontWeight: 600, color: '#f59e0b', background: 'rgba(245,158,11,0.15)', border: '1px solid #f59e0b', borderRadius: 4, padding: '1px 5px', marginRight: 6 }}>PENDING</span>
              )}
              {isEditable ? (
                <MemoizedEditableCell transaction={transaction} field="description"
                  displayValue={transaction.description || ''}
                  isEditing={isEditingThis('description')}
                  onStartEdit={startEditing} onCancel={cancelEditing} onSave={saveEdit} saving={saving} />
              ) : <span>{transaction.description || ''}</span>}
            </div>
          </div>
        );

      case 'user_description':
        return isEditable ? (
          <MemoizedEditableCell transaction={transaction} field="user_description"
            displayValue={transaction.user_description || ''}
            isEditing={isEditingThis('user_description')}
            onStartEdit={startEditing} onCancel={cancelEditing} onSave={saveEdit} saving={saving} />
        ) : <span>{transaction.user_description || ''}</span>;

      case 'amount':
        return isEditable ? (
          <MemoizedEditableCell transaction={transaction} field="amount"
            displayValue={formatCurrency(transaction.amount || 0)}
            isEditing={isEditingThis('amount')}
            onStartEdit={startEditing} onCancel={cancelEditing} onSave={saveEdit} saving={saving} />
        ) : <span>{formatCurrency(transaction.amount || 0)}</span>;

      case 'expense_category':
        return renderExpenseCategory(transaction);

      case 'source':
        return <span style={{ fontSize: '0.85rem', color: '#9ca3af' }}>{transaction.source_label || transaction.account_name || ''}</span>;

      case 'expense_group':
        return transaction.expense_group ? (
          <span style={{ fontSize: '0.75rem', background: '#1e3a5f', color: '#93c5fd', borderRadius: 4, padding: '2px 6px' }}>{transaction.expense_group}</span>
        ) : null;

      case 'asset_link': {
        const aid = transaction.linked_asset_id;
        if (!aid) {
          const cat = String(transaction.expense_category || '');
          const domain = getDomainForCategory(cat);
          const isYardWaste = cat.includes('yard_waste');
          const isWaste = (cat.startsWith('waste_') || cat.includes('sanitation')) && !isYardWaste;
          const isLinkable = domain !== 'generic' || isWaste || isYardWaste;
          if (!isLinkable) return null;
          let labelBtn = 'Link Asset';
          if (domain === 'utilities') labelBtn = 'Link Utilities';
          else if (domain === 'home') labelBtn = 'Link Home';
          else if (domain === 'landscaping' || isYardWaste) labelBtn = 'Link Landscaping';
          else if (domain === 'vehicle') labelBtn = 'Link Vehicle';
          else if (isWaste) labelBtn = 'Link Waste';
          return (
            <button className="btn btn-link btn-sm" style={{ color: '#93c5fd', padding: 0 }}
              onClick={(e) => { e.preventDefault(); setUtilityRow({ id: transaction.id, sourceType, date: transaction.transaction_date, description: transaction.description, amount: transaction.amount, linked_asset_id: transaction.linked_asset_id || '', linked_asset_expense_id: transaction.linked_asset_expense_id || '', linked_utility_bill_id: transaction.linked_utility_bill_id || '', linked_recurring_series_id: transaction.linked_recurring_series_id || '', linked_property_id: transaction.linked_property_id || '', expense_category: transaction.expense_category || '' }); setShowUtilityModal(true); }}>
              {labelBtn}
            </button>
          );
        }
        const label = buildTxnAssetLabel(transaction);
        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ color: '#60a5fa', fontSize: '0.9rem' }}>{label}</span>
            <button className="btn btn-link btn-sm" style={{ color: '#93c5fd', padding: 0 }}
              onClick={(e) => { e.preventDefault(); setUtilityRow({ id: transaction.id, sourceType, date: transaction.transaction_date, description: transaction.description, amount: transaction.amount, linked_asset_id: transaction.linked_asset_id || '', linked_asset_expense_id: transaction.linked_asset_expense_id || '', linked_utility_bill_id: transaction.linked_utility_bill_id || '', linked_recurring_series_id: transaction.linked_recurring_series_id || '', linked_property_id: transaction.linked_property_id || '', expense_category: transaction.expense_category || '', linked_todo_id: transaction.linked_todo_id || '' }); setShowUtilityModal(true); }}>
              Edit Link
            </button>
          </div>
        );
      }

      case 'linked_todo': {
        const todoInfo = transaction.linked_todo_info;
        if (!todoInfo) return null;
        let url = null;
        if (todoInfo.asset_type === 'vehicle') url = `/balance_sheet/assets/physical_assets/vehicles/${todoInfo.asset_id}?tab=todo`;
        else if (todoInfo.property_id) url = `/balance_sheet/assets/physical_assets/residential/${todoInfo.property_id}?tab=todo`;
        const statusColor = todoInfo.status === 'completed' ? '#22c55e' : todoInfo.status === 'in_progress' ? '#3b82f6' : '#9ca3af';
        const content = <span style={{ color: '#93c5fd', cursor: 'pointer', whiteSpace: 'nowrap', fontSize: '0.85rem' }} title={`To-Do: ${todoInfo.title}\nStatus: ${todoInfo.status_display}`}>{todoInfo.title}</span>;
        return (
          <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
            {url ? <Link to={url}>{content}</Link> : content}
            <span style={{ fontSize: '10px', color: statusColor, border: `1px solid ${statusColor}`, borderRadius: '4px', padding: '0 4px', marginLeft: '4px' }}>{todoInfo.status_display}</span>
          </span>
        );
      }

      case 'recurring_series': {
        const cat = String(transaction.expense_category || '');
        const seriesName = transaction.standalone_recurring_series_name || transaction.linked_recurring_series_name;
        const SKIP_CATS = new Set(['none', 'transfer', 'payments', 'financing_interest', 'financing_inter', 'financing_principal', 'taxes', 'insurance', 'income', 'rewards_cashback', 'reimbursement']);
        const isSkip = SKIP_CATS.has(cat) || cat.startsWith('income_');
        if (seriesName) {
          const sid = transaction.standalone_recurring_series_id;
          const assetSeriesId = transaction.linked_recurring_series_id;
          const seriesUrl = sid
            ? `/income_statement/expenses?view=recurring&tab=series&series_id=${sid}&series_type=standalone`
            : assetSeriesId ? `/income_statement/expenses?view=recurring&tab=series&series_id=${assetSeriesId}&series_type=asset` : null;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {seriesUrl ? <a href={seriesUrl} target="_blank" rel="noreferrer" style={{ color: '#93c5fd', fontSize: '0.85rem', whiteSpace: 'nowrap' }}>{seriesName}</a> : <span style={{ color: '#10b981', fontSize: '0.85rem' }}>{seriesName}</span>}
              {capabilities.standaloneSeriesModal && !transaction.linked_recurring_series_id && (
                <button className="btn btn-link btn-sm" style={{ color: '#93c5fd', padding: 0, fontSize: '0.75rem' }}
                  onClick={() => { setStandaloneSeriesRow({ id: transaction.id, sourceType, expense_category: cat, standalone_recurring_series_id: transaction.standalone_recurring_series_id || null, standalone_recurring_series_name: seriesName }); setShowStandaloneSeriesModal(true); }}>Edit</button>
              )}
            </div>
          );
        }
        if (capabilities.standaloneSeriesModal && !isSkip && cat) {
          return (
            <button className="btn btn-link btn-sm" style={{ color: '#6366f1', padding: 0, fontSize: '0.8rem', fontWeight: 500 }}
              onClick={() => { setStandaloneSeriesRow({ id: transaction.id, sourceType, expense_category: cat, standalone_recurring_series_id: null, standalone_recurring_series_name: null }); setShowStandaloneSeriesModal(true); }}>
              + Recurring
            </button>
          );
        }
        return <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.85rem' }}>—</span>;
      }

      case 'debt_link':
        return transaction.linked_debt_name ? (
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <span style={{ color: '#fbbf24', fontSize: '0.85rem' }}>{transaction.linked_debt_name}</span>
            {transaction.linked_debt_payment_id && <small style={{ color: '#9ca3af', fontSize: '0.7rem' }}>Payment ID: {transaction.linked_debt_payment_id}</small>}
          </div>
        ) : <span style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.85rem' }}>None</span>;

      case 'receipt':
        if (!capabilities.receipts) return null;
        return (transaction.has_receipt || transaction.receipt_url || transaction.receipt) ? (
          <button className="btn btn-sm btn-outline-primary" onClick={() => openReceipt(transaction)}>View</button>
        ) : (
          <label className="btn btn-sm btn-outline-secondary mb-0">
            Upload
            <input type="file" accept="image/*,application/pdf" style={{ display: 'none' }}
              onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadReceipt(transaction.id, f); e.target.value = ''; }} />
          </label>
        );

      case 'actions': {
        const cat = String(transaction.expense_category || '');
        const isIncome = cat === 'income' || cat.startsWith('income_');
        const isDebt = cat === 'debt_payment' || cat.includes('financing') || cat.includes('interest');
        const isDebtDisb = cat === 'debt_disbursement';
        return (
          <div className="dropdown">
            <button className="btn btn-sm btn-outline-secondary dropdown-toggle" type="button" data-bs-toggle="dropdown">Actions</button>
            <ul className="dropdown-menu dropdown-menu-dark">
              {isIncome && capabilities.incomeSplit && (
                <li><button className="dropdown-item" onClick={() => { setIncomeSplitRow({ id: transaction.id, amount: transaction.amount, date: transaction.transaction_date, description: transaction.description, sourceType }); setIncomeSplitDefaultKind('recurring'); setShowIncomeSplitModal(true); }}>Link to Income Stream</button></li>
              )}
              {isIncome && capabilities.incomeLink && (
                <li><button className="dropdown-item" onClick={() => { setIncomeLinkRow({ id: transaction.id, amount: transaction.amount, date: transaction.transaction_date, description: transaction.description, sourceType }); setShowIncomeLinkModal(true); }}>Link as General Income</button></li>
              )}
              {(isDebt || isDebtDisb) && capabilities.debtDisbursement && (
                <li><button className="dropdown-item" onClick={() => { setDebtDisbursementRow(transaction); setShowDebtDisbursementModal(true); }}>Link to Debt Disbursement</button></li>
              )}
              {cat === 'reimbursement' && capabilities.reimbursement && (
                <li><button className="dropdown-item" onClick={() => { setReimbursementRow(transaction); setShowReimbursementModal(true); }}>Link Reimbursement</button></li>
              )}
              {capabilities.split && (
                <li><button className="dropdown-item" onClick={() => { setSplitRow({ id: transaction.id, sourceType, amount: transaction.amount, description: transaction.description, transaction_date: transaction.transaction_date }); setShowSplitModal(true); }}>Split Transaction</button></li>
              )}
              {capabilities.delete && <li><hr className="dropdown-divider" /></li>}
              {capabilities.delete && (
                <li><button className="dropdown-item text-danger" onClick={() => handleDelete(transaction.id)} disabled={deleting.has(transaction.id)}>{deleting.has(transaction.id) ? 'Deleting…' : 'Delete'}</button></li>
              )}
            </ul>
          </div>
        );
      }

      default:
        return null;
    }
  };

  // ── Render ──
  return (
    <>
      {/* Export button */}
      {capabilities.export && (
        <div className="d-flex justify-content-end mb-2">
          <button className="btn btn-sm btn-outline-success" onClick={handleExport}>
            📊 Export to Excel
          </button>
        </div>
      )}

      <div className="table-responsive">
        <table className="table table-dark table-hover table-sm align-middle">
          <thead>
            <tr>
              {columns.map(colKey => (
                <th key={colKey}
                  scope="col"
                  style={colHasSort(colKey) ? { cursor: 'pointer', whiteSpace: 'nowrap' } : { whiteSpace: 'nowrap' }}
                  onClick={colHasSort(colKey) ? () => handleSort(colKey) : undefined}
                >
                  {colHeader(colKey)}
                  {colHasSort(colKey) && <SortIcon field={colKey} sortField={sortField} sortDirection={sortDirection} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedTransactions.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center text-muted py-4">
                  No transactions found for the selected period.
                </td>
              </tr>
            ) : (
              sortedTransactions
                .filter(transaction => {
                  // Always show parent rows; only show child rows if their parent is expanded
                  if (!transaction._isChild) return true;
                  const parentId = transaction.parent ?? transaction.parent_id ?? null;
                  return parentId != null && expandedTxns.has(parentId);
                })
                .map((transaction) => {
                  const hasChildren = !transaction._isChild && sortedTransactions.some(
                    t => t._isChild && (t.parent ?? t.parent_id) === transaction.id
                  );
                  const isExpanded = expandedTxns.has(transaction.id);
                  return (
                    <tr
                      key={transaction.id}
                      className={deleting.has(transaction.id) ? 'table-secondary' : ''}
                      style={{
                        backgroundColor: transaction._isChild
                          ? (transaction._isReimbursementChild || transaction._isReimbursementOriginal ? '#0f2a1a' : '#1f2a44')
                          : transaction.is_pending ? '#2a2a1a' : 'transparent',
                        borderLeft: transaction._isReimbursementChild ? '3px solid #34d399'
                          : transaction._isReimbursementOriginal ? '3px solid #6ee7b7'
                          : transaction._isChild ? '3px solid #60a5fa'
                          : transaction.is_pending ? '3px solid #f59e0b' : 'none',
                        opacity: transaction.is_pending ? 0.85 : 1,
                      }}
                    >
                      {columns.map((colKey, colIdx) => (
                        <td key={colKey}
                          style={colKey === 'expense_category' ? { minWidth: '220px', whiteSpace: 'nowrap' } : undefined}
                        >
                          {/* Expand/collapse toggle on first column of parent rows that have children */}
                          {colIdx === 0 && hasChildren && (
                            <button
                              className="btn btn-sm btn-link p-0 me-1"
                              style={{ color: '#60a5fa', fontSize: '0.75rem', lineHeight: 1, verticalAlign: 'middle' }}
                              onClick={() => toggleExpand(transaction.id)}
                              title={isExpanded ? 'Collapse child transactions' : 'Expand child transactions'}
                            >
                              {isExpanded ? '▼' : '▶'}
                            </button>
                          )}
                          {/* Indent child rows on first column */}
                          {colIdx === 0 && transaction._isChild && (
                            <span style={{ display: 'inline-block', width: '20px' }} />
                          )}
                          {renderCell(transaction, colKey)}
                        </td>
                      ))}
                    </tr>
                  );
                })
            )}
          </tbody>
        </table>
      </div>

      {/* ── Modals ── */}
      {showUtilityModal && utilityRow && (
        <AssetExpenseLinkModal
          visible={showUtilityModal}
          onClose={() => { setShowUtilityModal(false); setUtilityRow(null); }}
          sourceType={sourceType}
          transaction={utilityRow}
          assetOptions={allAssets.map(a => ({ id: a.id, label: buildAssetLabel(a, assetMaps) }))}
          onSaved={async () => { setShowUtilityModal(false); setUtilityRow(null); if (onRefresh) await onRefresh(); }}
          onUnlinked={async () => { setShowUtilityModal(false); setUtilityRow(null); if (onRefresh) await onRefresh(); }}
        />
      )}

      {capabilities.split && (
        <SplitTransactionModal
          isOpen={showSplitModal}
          transaction={splitRow}
          onClose={() => { setShowSplitModal(false); setSplitRow(null); }}
          onSaveSuccess={async () => { setShowSplitModal(false); setSplitRow(null); if (onRefresh) await onRefresh(); }}
          formatCurrency={formatCurrency}
        />
      )}

      {capabilities.incomeSplit && showIncomeSplitModal && incomeSplitRow && (
        <IncomeSplitModal
          row={incomeSplitRow}
          defaultKind={incomeSplitDefaultKind}
          onClose={() => { setShowIncomeSplitModal(false); setIncomeSplitRow(null); }}
          onCompleted={async () => { setShowIncomeSplitModal(false); setIncomeSplitRow(null); if (onRefresh) await onRefresh(); }}
        />
      )}

      {capabilities.incomeLink && showIncomeLinkModal && incomeLinkRow && (
        <IncomeLinkModal
          row={incomeLinkRow}
          onClose={() => { setShowIncomeLinkModal(false); setIncomeLinkRow(null); }}
          onLinked={async () => { setShowIncomeLinkModal(false); setIncomeLinkRow(null); if (onRefresh) await onRefresh(); }}
          apiPath={sourceType === 'revolver' ? '/balance_sheet/financing/revolvers/transactions/' : '/balance_sheet/assets/cash_accounts/transactions/'}
        />
      )}

      {capabilities.debtDisbursement && showDebtDisbursementModal && debtDisbursementRow && (
        <DebtDisbursementLinkModal
          row={debtDisbursementRow}
          onClose={() => { setShowDebtDisbursementModal(false); setDebtDisbursementRow(null); }}
          onLinked={async () => { setShowDebtDisbursementModal(false); setDebtDisbursementRow(null); if (onRefresh) await onRefresh(); }}
        />
      )}

      {capabilities.reimbursement && showReimbursementModal && reimbursementRow && (
        <ReimbursementLinkModal
          isOpen={showReimbursementModal}
          transaction={{ ...reimbursementRow, type: sourceType === 'revolver' ? 'revolver' : 'cash' }}
          onClose={() => { setShowReimbursementModal(false); setReimbursementRow(null); }}
          onSaveSuccess={async () => { setShowReimbursementModal(false); setReimbursementRow(null); if (onRefresh) await onRefresh(); }}
          formatCurrency={formatCurrency}
        />
      )}

      {capabilities.standaloneSeriesModal && (
        <StandaloneSeriesModal
          isOpen={showStandaloneSeriesModal}
          row={standaloneSeriesRow}
          onClose={() => { setShowStandaloneSeriesModal(false); setStandaloneSeriesRow(null); }}
          onSaveSuccess={async () => { setShowStandaloneSeriesModal(false); setStandaloneSeriesRow(null); if (onRefresh) await onRefresh(); }}
        />
      )}

      {capabilities.receipts && (
        <ReceiptModal
          show={showReceiptModal}
          previewUrl={receiptPreviewUrl}
          title={receiptModalTitle}
          onClose={closeReceipt}
        />
      )}
    </>
  );
};

export default CoreTransactionTable;
